import { useTranslation } from 'react-i18next'
import { Button, H3, Text } from '@/components/atoms'
import {
  CoverageSewerBackupIcon,
  CoveragePersonalLiabilityIcon,
  CoverageTheftAccidentsIcon,
  CoverageDamageToPropertyIcon,
  CoverageFireWaterDamageIcon,
  CoverageLossOfUseIcon,
  CoverageJewelryIcon,
  CoverageFineArtIcon,
  CoverageElectronicsIcon,
  CoverageInstrumentsIcon,
  CoverageDesignerClothingIcon,
  CoverageSportingEquipmentIcon,
  CoverageTheftManIcon,
  CoverageCashBulbIcon,
} from '@/components/atoms/icons/coverage-feature'
import { CoverageBanner, IconTile, Slider } from '@/components/molecules'
import { useRentersStepThreePage } from './page.onboarding-renters-step-three.hook'

const COVERAGE_ICON_CLASS = 'text-indigo-base size-12.5'

interface CoverageItem {
  label: string
  icon: React.ReactNode
}

interface RentersStepThreePageProps {
  policyId: string
  startAt: string
  dwellingType: string
  isLoading?: boolean
  onContinue: () => void
  onBack: () => void
  additionalCoverageTypes?: string[]
}

export function RentersStepThreePage({
  policyId,
  startAt,
  dwellingType,
  isLoading = false,
  onContinue,
  onBack,
  additionalCoverageTypes = [],
}: RentersStepThreePageProps) {
  const { t } = useTranslation()
  const { validAdditionalCoverageTypes, hasAdditionalCoverage } =
    useRentersStepThreePage({ additionalCoverageTypes })

  const essentialItems: CoverageItem[] = [
    {
      label: t('onboarding.renters.stepThree.coverageItems.sewerBackup'),
      icon: <CoverageSewerBackupIcon className={COVERAGE_ICON_CLASS} />,
    },
    {
      label: t('onboarding.renters.stepThree.coverageItems.personalLiability'),
      icon: <CoveragePersonalLiabilityIcon className={COVERAGE_ICON_CLASS} />,
    },
    {
      label: t('onboarding.renters.stepThree.coverageItems.theftAccidents'),
      icon: <CoverageTheftAccidentsIcon className={COVERAGE_ICON_CLASS} />,
    },
    {
      label: t('onboarding.renters.stepThree.coverageItems.damageToProperty'),
      icon: <CoverageDamageToPropertyIcon className={COVERAGE_ICON_CLASS} />,
    },
    {
      label: t('onboarding.renters.stepThree.coverageItems.fireWaterDamage'),
      icon: <CoverageFireWaterDamageIcon className={COVERAGE_ICON_CLASS} />,
    },
    {
      label: t('onboarding.renters.stepThree.coverageItems.lossOfUse'),
      icon: <CoverageLossOfUseIcon className={COVERAGE_ICON_CLASS} />,
    },
  ]

  const additionalCoverageItemMap: Record<string, CoverageItem> = {
    ReplacementCost: {
      label: t(
        'onboarding.renters.stepThree.additionalCoverageItems.replacementCost',
      ),
      icon: <CoverageCashBulbIcon className={COVERAGE_ICON_CLASS} />,
    },
    FraudProtection: {
      label: t(
        'onboarding.renters.stepThree.additionalCoverageItems.fraudProtection',
      ),
      icon: <CoverageTheftManIcon className={COVERAGE_ICON_CLASS} />,
    },
    WaterSewerBackup: {
      label: t(
        'onboarding.renters.stepThree.additionalCoverageItems.waterSewerBackup',
      ),
      icon: <CoverageSewerBackupIcon className={COVERAGE_ICON_CLASS} />,
    },
  }

  const additionalCoverageItems: CoverageItem[] = validAdditionalCoverageTypes
    .map((type) => additionalCoverageItemMap[type])
    .filter((item): item is CoverageItem => !!item)

  const scheduledItems: CoverageItem[] = [
    {
      label: t('onboarding.renters.stepThree.scheduledCoverageItems.jewelry'),
      icon: <CoverageJewelryIcon className={COVERAGE_ICON_CLASS} />,
    },
    {
      label: t(
        'onboarding.renters.stepThree.scheduledCoverageItems.fineArtAndCollectibles',
      ),
      icon: <CoverageFineArtIcon className={COVERAGE_ICON_CLASS} />,
    },
    {
      label: t(
        'onboarding.renters.stepThree.scheduledCoverageItems.electronics',
      ),
      icon: <CoverageElectronicsIcon className={COVERAGE_ICON_CLASS} />,
    },
    {
      label: t(
        'onboarding.renters.stepThree.scheduledCoverageItems.instruments',
      ),
      icon: <CoverageInstrumentsIcon className={COVERAGE_ICON_CLASS} />,
    },
    {
      label: t(
        'onboarding.renters.stepThree.scheduledCoverageItems.designerClothing',
      ),
      icon: <CoverageDesignerClothingIcon className={COVERAGE_ICON_CLASS} />,
    },
    {
      label: t(
        'onboarding.renters.stepThree.scheduledCoverageItems.sportingEquipment',
      ),
      icon: <CoverageSportingEquipmentIcon className={COVERAGE_ICON_CLASS} />,
    },
  ]

  const renderIconGrid = (items: CoverageItem[]) => (
    <div className="grid grid-cols-3 gap-4">
      {items.map((item, i) => (
        <IconTile
          key={i}
          icon={item.icon}
          label={item.label}
          iconBgColor="bg-white"
        />
      ))}
    </div>
  )

  const renderSectionTitle = (title: string) => (
    <Text className="text-xs font-bold text-neutral-900">{title}</Text>
  )

  const customSlides = hasAdditionalCoverage && [
    { content: renderIconGrid(essentialItems) },
    {
      content: (
        <div className="flex flex-col gap-3">
          {renderSectionTitle(t('onboarding.renters.stepThree.scheduledItems'))}
          {renderIconGrid(scheduledItems)}
        </div>
      ),
    },
    {
      content: (
        <div className="flex flex-col gap-3">
          {renderSectionTitle(
            t('onboarding.renters.stepThree.additionalCoverage'),
          )}
          {renderIconGrid(additionalCoverageItems)}
        </div>
      ),
    },
  ]

  const basicCoverageItems: CoverageItem[] = [
    ...essentialItems,
    ...scheduledItems,
  ]

  return (
    <div className="flex h-full flex-col">
      <div className="min-h-0 flex-1 overflow-y-auto pb-4 md:flex md:flex-row md:justify-between md:pb-0">
        <div className="flex flex-col justify-between gap-6 md:max-w-98">
          <div className="flex flex-col gap-4 md:max-w-90 md:gap-6">
            <H3 className="text-neutral-900">
              {t('onboarding.renters.stepThree.title')}
            </H3>
            <Text className="text-sm text-neutral-900 md:text-base">
              {t('onboarding.renters.stepThree.description')}
            </Text>
            <CoverageBanner
              planName={dwellingType}
              policyId={policyId}
              startDate={startAt}
              isLoading={isLoading}
            />
          </div>

          <div className="hidden gap-3 md:mb-2.5 md:flex">
            <Button
              variant="secondary"
              onClick={onBack}
              data-testid="step-three-back"
            >
              {t('onboarding.back')}
            </Button>
            <Button onClick={onContinue} data-testid="step-three-continue">
              {t('onboarding.continue')}
            </Button>
          </div>
        </div>

        <div className="mt-8 flex justify-center md:mt-0 md:mr-7.5 md:max-w-69 md:justify-end">
          {customSlides ? (
            <Slider
              items={customSlides}
              itemsPerSlideMobile={1}
              itemsPerSlideDesktop={1}
              layout="grid"
              className="w-full justify-center md:gap-8"
              dotsClassName="md:mb-2.5 mt-auto"
              renderItem={(slide) => (
                <div className="col-span-3">{slide.content}</div>
              )}
            />
          ) : (
            <Slider
              items={basicCoverageItems}
              layout="grid"
              className="w-full justify-center md:gap-8"
              renderItem={(item, index) => (
                <IconTile
                  key={index}
                  icon={item.icon}
                  label={item.label}
                  iconBgColor="bg-white"
                />
              )}
            />
          )}
        </div>
      </div>

      <div className="flex w-full gap-3 pt-4 md:hidden">
        <Button
          variant="secondary"
          onClick={onBack}
          className="h-12 w-full"
          data-testid="step-three-back"
        >
          {t('onboarding.back')}
        </Button>
        <Button
          onClick={onContinue}
          className="h-12 w-full"
          data-testid="step-three-continue"
        >
          {t('onboarding.continue')}
        </Button>
      </div>
    </div>
  )
}
